import { NavLink } from 'react-router-dom';
import { Building2, BarChart3 } from 'lucide-react';
import SidebarMobileOverlay from './SidebarMobileOverlay';
import SidebarTopControls from './SidebarTopControls';
import SidebarAccountMenu from './SidebarAccountMenu';

interface SuperAdminSidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  onClose: () => void;
  onToggleCollapse: () => void;
  user: {
    name: string;
    email: string;
    avatar?: string | null;
  };
  onLogout: () => void;
}

const NAV_LINKS = [
  { to: '/accounts', label: 'Accounts', icon: Building2, testid: 'superadmin-nav-accounts' },
  { to: '/token-usage', label: 'Token Usage', icon: BarChart3, testid: 'superadmin-nav-token-usage' },
] as const;

/**
 * Dedicated super-admin console sidebar — deliberately thin (two links: the
 * accounts list and the token-usage dashboard). Rendered only inside
 * SuperAdminLayout, which is SUPER_ADMIN-gated via SuperAdminGate, so it
 * carries none of the tenant-admin or learner chrome. Shares its generic
 * chrome (mobile overlay, collapse toggle, bottom account menu) with
 * AdminSidebar via SidebarMobileOverlay/SidebarTopControls/SidebarAccountMenu
 * — only the nav-link list below is specific to this console.
 */
export default function SuperAdminSidebar({
  isOpen,
  isCollapsed,
  onClose,
  onToggleCollapse,
  user,
  onLogout,
}: SuperAdminSidebarProps) {
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-4 py-3 rounded-lg transition-colors ${
      isActive
        ? 'bg-tutara-primary text-white'
        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg'
    }`;

  return (
    <>
      <SidebarMobileOverlay isOpen={isOpen} onClose={onClose} />

      <aside
        className={`fixed top-0 left-0 z-30 h-screen bg-white dark:bg-tutara-dark-mode-surface border-r border-gray-200 dark:border-tutara-dark-mode-border transform transition-all duration-300 lg:translate-x-0 lg:top-16 lg:h-[calc(100vh-4rem)] flex flex-col overflow-visible ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isCollapsed ? 'w-16' : 'w-64'}`}
      >
        <SidebarTopControls onClose={onClose} isCollapsed={isCollapsed} onToggleCollapse={onToggleCollapse} />

        {/* Navigation — the whole platform console: Accounts + Token Usage */}
        <nav className={`p-4 space-y-2 overflow-y-auto flex-1 ${isCollapsed ? 'px-2' : ''}`}>
          {NAV_LINKS.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={testid}
              to={to}
              title={isCollapsed ? label : undefined}
              data-testid={testid}
              className={linkClass}
              onClick={onClose}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {!isCollapsed && <span className="font-medium">{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Account Section — Fixed at bottom (platform-owner identity + logout) */}
        <SidebarAccountMenu user={user} onLogout={onLogout} isCollapsed={isCollapsed} accountLabel="Platform Console" />
      </aside>
    </>
  );
}
